import { useState, type ReactNode } from 'react';
import { FileSpreadsheet, FileText, RotateCcw } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { ExportTemplate } from '@/lib/export';

interface TemplatePickerProps {
  /** Saved templates to list. Omit for a picker that carries only `footer` actions. */
  templates?: ExportTemplate[];
  lastTemplateId?: string | null;
  onPick?: (t: ExportTemplate) => void;
  /** The clickable element that opens the picker. */
  trigger: ReactNode;
  /** Optional hover tooltip for the trigger (needs a TooltipProvider ancestor). */
  tooltip?: string;
  align?: 'start' | 'end';
  /**
   * Accessible name for the popover. Radix gives the content `role="dialog"`, and
   * a dialog with no name is announced as just "dialog" — worth passing whenever
   * the popover carries actions instead of a searchable list.
   */
  contentLabel?: string;
  /** Optional pinned actions below the (searchable) list — e.g. quick Excel/CSV. */
  footer?: (close: () => void) => ReactNode;
}

/**
 * Searchable template chooser. Scales to many templates: type to filter, the
 * last-used template is pinned on top, and the list scrolls within a fixed height.
 */
export function TemplatePicker({
  templates = [],
  lastTemplateId,
  onPick,
  trigger,
  tooltip,
  align = 'end',
  contentLabel,
  footer,
}: TemplatePickerProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const last = templates.find((t) => t.id === lastTemplateId) ?? null;
  const rest = templates.filter((t) => t.id !== last?.id);

  const row = (t: ExportTemplate, icon: ReactNode) => (
    <CommandItem
      key={t.id}
      value={t.name}
      onSelect={() => {
        onPick?.(t);
        close();
      }}
      className="cursor-pointer"
    >
      {icon}
      <span className="flex-1 truncate">{t.name}</span>
      <span className="ml-2 text-xs text-muted-foreground">{t.format.toUpperCase()}</span>
    </CommandItem>
  );

  const formatIcon = (t: ExportTemplate) =>
    t.format === 'pdf' ? (
      <FileText className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
    ) : (
      <FileSpreadsheet className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      <PopoverContent align={align} aria-label={contentLabel} className="w-64 p-0">
        {templates.length > 0 && (
          <Command>
            <CommandInput placeholder="Search templates…" className="h-9" />
            <CommandList className="max-h-72">
              <CommandEmpty className="py-4 text-center text-sm text-muted-foreground">
                No templates found
              </CommandEmpty>
              {last && (
                <CommandGroup heading="Last used">
                  {row(last, <RotateCcw className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />)}
                </CommandGroup>
              )}
              {rest.length > 0 && (
                <CommandGroup heading={last ? 'Templates' : undefined}>
                  {rest.map((t) => row(t, formatIcon(t)))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        )}
        {footer && (
          <div className={cn('p-1', templates.length > 0 && 'border-t border-border')}>
            {footer(close)}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
